import React from 'react';
import { Car, ExternalLink, ShieldCheck } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { spotHeroUrl } from '@/config/affiliates';

/**
 * SpotHero parking widget — displayed on every airport guide page.
 * Deep-links visitors to SpotHero's search for that specific airport;
 * we earn ~$3-5 per booking once the SpotHero/Impact affiliate ID is
 * configured in `frontend/.env` (see /app/frontend/src/config/affiliates.js).
 */
export default function ParkingWidget({ airportCode, airportName, city }) {
  if (!airportCode) return null;
  const url = spotHeroUrl(airportCode, { airportName });
  const label = city ? `${airportCode} — ${city}` : airportCode;

  return (
    <Card
      className="border-slate-200 bg-white shadow-sm overflow-hidden"
      data-testid="parking-widget"
    >
      <CardContent className="p-0">
        <div className="grid sm:grid-cols-[1.4fr_1fr]">
          {/* Left — pitch */}
          <div className="p-5 sm:p-6">
            <div className="flex items-center gap-2 mb-2">
              <span className="inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-emerald-700 bg-emerald-50 border border-emerald-100 px-2 py-0.5 rounded-full">
                <Car className="w-3 h-3" /> Airport parking
              </span>
              <span className="text-[10px] font-medium uppercase tracking-wider text-slate-400">
                Partner
              </span>
            </div>
            <h2 className="heading text-lg sm:text-xl font-semibold text-slate-900 leading-snug mb-2">
              Reserve parking at {label} — often 30-70% cheaper than drive-up
            </h2>
            <p className="text-sm text-slate-600 leading-relaxed mb-4">
              SpotHero shows every off-airport and on-airport lot side-by-side
              with real prices, walk times, and shuttle info. Book on your
              phone, drive in, scan the pass. No lines, no printing.
            </p>
            <ul className="text-xs text-slate-500 space-y-1 mb-5">
              <li className="flex items-start gap-2">
                <ShieldCheck className="w-3.5 h-3.5 mt-0.5 text-emerald-600 flex-shrink-0" />
                Free cancellation on most spots
              </li>
              <li className="flex items-start gap-2">
                <ShieldCheck className="w-3.5 h-3.5 mt-0.5 text-emerald-600 flex-shrink-0" />
                Guaranteed rate — no drive-up surprises
              </li>
              <li className="flex items-start gap-2">
                <ShieldCheck className="w-3.5 h-3.5 mt-0.5 text-emerald-600 flex-shrink-0" />
                Compare valet, self-park, and covered lots
              </li>
            </ul>
            <a
              href={url}
              target="_blank"
              rel="sponsored nofollow noopener"
              data-testid="parking-widget-cta"
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-slate-900 hover:bg-slate-800 text-white rounded-lg text-sm font-medium transition-colors"
            >
              See {airportCode} parking on SpotHero
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
            <p className="text-[10px] text-slate-400 mt-3">
              Affiliate link — we may earn a commission if you book, at no
              extra cost to you.
            </p>
          </div>

          {/* Right — boarding-pass style price teaser */}
          <div className="hidden sm:flex flex-col justify-center bg-slate-900 text-white p-5 sm:p-6 relative">
            <div className="absolute inset-y-4 left-0 w-2 flex flex-col justify-between">
              {[...Array(10)].map((_, i) => (
                <span key={i} className="w-2 h-2 rounded-full bg-slate-50" />
              ))}
            </div>
            <div className="pl-4">
              <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-slate-400 mb-1">
                Typical range
              </p>
              <p className="text-3xl font-bold heading tabular-nums leading-none mb-1">
                $8<span className="text-lg text-slate-400">–</span>28
              </p>
              <p className="text-xs text-slate-400">per day, near {airportCode}</p>
              <div className="mt-4 pt-4 border-t border-slate-700">
                <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-slate-400 mb-1">
                  Vs. drive-up
                </p>
                <p className="text-xl font-semibold heading tabular-nums text-emerald-400">
                  Save 30-70%
                </p>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
